import { frameId } from "../assets/manifest.ts";
import type { PropInstance, WorldDocument } from "../document/types.ts";
import type { ObjectTypeDefinition } from "./objects.ts";
import { presenceInsideOf } from "./presence.ts";

/**
 * Whether a frame's Behind and In front marks can change anything in the open world (ADR-132 decision 5): the compile
 * lays the marks of the props a client draws as props, those without a behaviour and those driven by presence, and
 * leaves out a thing the server moves (a gate or a well), which its own view draws by the base line. A behaviour whose
 * type has one state (a sign, an info board) changes neither its frame nor its blocking, so it is painted like a plain
 * prop (decision 13).
 */

/**
 * Whether an object type holds still: it has one state, so a prop of it shows one frame and blocks one way whatever
 * the server does, and its marks are laid like a plain prop's.
 *
 * @param type The registered type, or undefined for one the game did not register.
 * @returns True when the type has exactly one state.
 */
export function holdsStill(type: { readonly states: readonly unknown[] } | undefined): boolean {
  return type !== undefined && type.states.length === 1;
}

/**
 * The frames a placed prop shows: its own and, for a behaviour, the frame of each of its states.
 *
 * @param prop The placed prop.
 * @returns The frame ids.
 */
function framesShown(prop: PropInstance): string[] {
  const states = Object.values(prop.behaviour?.frames ?? {}).map((ref) => frameId(ref.tileset, null, ref.tile));
  return [frameId(prop.frame.tileset, null, prop.frame.tile), ...states];
}

/**
 * Whether a world places a frame, and places it only as things the server moves: no prop without a behaviour and no
 * prop driven by presence stands on it, and at least one thing whose behaviour is not driven by presence shows it.
 *
 * @param world The world.
 * @param frame The frame id.
 * @param types The registered object types, which say which behaviours presence drives.
 * @returns True when the frame's marks reach no stand table of the world.
 */
export function placedOnlyAsDriven(world: WorldDocument, frame: string, types: readonly ObjectTypeDefinition[]): boolean {
  let driven = false;
  for (const map of world.maps) {
    for (const layer of map.layers) {
      if (layer.kind !== "prop") {
        continue;
      }
      for (const prop of layer.props) {
        const moved = prop.behaviour !== undefined && presenceInsideOf(types, prop) === null && !holdsStill(types.find((t) => t.type === prop.behaviour?.type));
        if (moved) {
          driven ||= framesShown(prop).includes(frame);
        } else if (frameId(prop.frame.tileset, null, prop.frame.tile) === frame) {
          return false;
        }
      }
    }
  }
  return driven;
}
